import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { submitLead, toEventPayload } from '../api/leadGeneration'
import PageStatus from '../components/PageStatus'
import PhoneInputField from '../components/PhoneInputField'
import { usePublicPage } from '../hooks/usePublicPage'
import { formatEventSchedule } from '../lib/eventDate'
import { getSessionId } from '../lib/session'
import { getUtmParams } from '../lib/utm'
import { isValidEmail, isValidPhone } from '../lib/validation'
import styles from './EventPage.module.css'

const agentPropType = PropTypes.shape({
  name: PropTypes.string,
  mobile: PropTypes.string,
  email: PropTypes.string,
})

const pagePropType = PropTypes.shape({
  publicKey: PropTypes.string,
  pageTitle: PropTypes.string,
  pageTypeName: PropTypes.string,
  eventName: PropTypes.string,
  eventStartDateTime: PropTypes.string,
  eventEndDateTime: PropTypes.string,
  eventAddress: PropTypes.string,
  eventOnlineLink: PropTypes.string,
  agent: agentPropType,
})

const EMPTY_FORM = { firstName: '', lastName: '', email: '', phone: '' }
const FIELD_ORDER = ['firstName', 'lastName', 'email', 'phone']

function validate(values) {
  const errors = {}
  if (!values.firstName.trim()) errors.firstName = 'First name is required.'
  if (!values.lastName.trim()) errors.lastName = 'Last name is required.'
  if (!values.email.trim()) errors.email = 'Email is required.'
  else if (!isValidEmail(values.email)) errors.email = 'Enter a valid email address.'
  if (!values.phone.trim()) errors.phone = 'Phone is required.'
  else if (!isValidPhone(values.phone)) errors.phone = 'Enter a valid phone number.'
  return errors
}

function mapUrl(address) {
  return `https://maps.google.com/?q=${encodeURIComponent(address)}`
}

function PinIcon({ color = '#F5B83D', size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </svg>
  )
}

PinIcon.propTypes = {
  color: PropTypes.string,
  size: PropTypes.number,
}

function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F5B83D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M16 3v4M8 3v4M3 10h18" />
    </svg>
  )
}

function LinkIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F5B83D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 13a5 5 0 0 0 7.1 0l3-3a5 5 0 0 0-7.1-7.1l-1.7 1.7" />
      <path d="M14 11a5 5 0 0 0-7.1 0l-3 3a5 5 0 0 0 7.1 7.1l1.7-1.7" />
    </svg>
  )
}

function initials(name) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

function AgentCard({ agent }) {
  const name = agent && agent.name && agent.name.trim()
  const phone = agent && agent.mobile && agent.mobile.trim()
  const email = agent && agent.email && agent.email.trim()
  if (!name && !phone && !email) return null

  const contact = [phone, email].filter(Boolean).join(' · ')

  return (
    <section className={styles.agent} aria-label="Your agent">
      <div className={styles.avatar} aria-hidden="true">
        {initials(name || '')}
      </div>
      <div className={styles.agentInfo}>
        <div className={styles.agentLabel}>Your agent today</div>
        {name && <div className={styles.agentName}>{name}</div>}
        {contact && <div className={styles.agentContact}>{contact}</div>}
      </div>
    </section>
  )
}

AgentCard.propTypes = {
  agent: agentPropType,
}

function EventDetails({ page }) {
  const schedule = formatEventSchedule(page.eventStartDateTime, page.eventEndDateTime)

  return (
    <>
      {schedule && (
        <div className={styles.detail}>
          <CalendarIcon />
          <span>{schedule}</span>
        </div>
      )}
      {page.eventAddress && (
        <a className={styles.detail} href={mapUrl(page.eventAddress)} target="_blank" rel="noopener noreferrer">
          <PinIcon />
          <span>
            {page.eventAddress}
            <span className={styles.detailLink}>Map</span>
          </span>
        </a>
      )}
      {page.eventOnlineLink && (
        <a className={styles.detail} href={page.eventOnlineLink} target="_blank" rel="noopener noreferrer">
          <LinkIcon />
          <span>
            Online event
            <span className={styles.detailLink}>Join link</span>
          </span>
        </a>
      )}
    </>
  )
}

EventDetails.propTypes = {
  page: pagePropType.isRequired,
}

function SignInForm({ page, onSuccess }) {
  const formRef = useRef(null)
  const [values, setValues] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const updateField = (name, value) => {
    const next = { ...values, [name]: value }
    setValues(next)
    if (touched[name]) {
      setErrors((current) => ({ ...current, [name]: validate(next)[name] }))
      setTouched((current) => ({ ...current, [name]: true }))
    }
  }

  const handleBlur = (event) => {
    const { name } = event.target
    setTouched((current) => ({ ...current, [name]: true }))
    setErrors((current) => ({ ...current, [name]: validate(values)[name] }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (submitting) return

    const nextErrors = validate(values)
    setErrors(nextErrors)
    setTouched(Object.fromEntries(FIELD_ORDER.map((name) => [name, true])))
    setSubmitError('')

    const firstInvalid = FIELD_ORDER.find((name) => nextErrors[name])
    if (firstInvalid) {
      formRef.current.querySelector(`[name="${firstInvalid}"]`)?.focus()
      return
    }

    setSubmitting(true)
    try {
      await submitLead(page.publicKey, {
        ...toEventPayload(values),
        sessionId: getSessionId(),
        ...getUtmParams(),
      })
      onSuccess()
    } catch (error) {
      setSubmitError(error.message || 'Something went wrong. Please try again.')
      setSubmitting(false)
    }
  }

  const textField = (name, label, type, autoComplete, inputMode) => (
    <label className={styles.field}>
      {label} *
      <input
        type={type}
        name={name}
        value={values[name]}
        autoComplete={autoComplete}
        inputMode={inputMode}
        required
        aria-invalid={errors[name] ? 'true' : undefined}
        aria-describedby={errors[name] ? `${name}-error` : undefined}
        onChange={(event) => updateField(name, event.target.value)}
        onBlur={handleBlur}
      />
      {errors[name] && (
        <span className={styles.error} id={`${name}-error`}>
          {errors[name]}
        </span>
      )}
    </label>
  )

  return (
    <form ref={formRef} className={styles.card} onSubmit={handleSubmit} noValidate>
      <div>
        <h2 className={styles.cardTitle}>Sign in for this event</h2>
        <p className={styles.cardIntro}>
          So we can follow up with details on this event. All fields are required.
        </p>
      </div>

      <div className={styles.grid2}>
        {textField('firstName', 'First name', 'text', 'given-name')}
        {textField('lastName', 'Last name', 'text', 'family-name')}
      </div>
      {textField('email', 'Email', 'email', 'email', 'email')}
      <label className={styles.field}>
        Phone *
        <PhoneInputField
          name="phone"
          value={values.phone}
          required
          invalid={Boolean(errors.phone)}
          country="ca"
          className={`${styles.phoneInput}${errors.phone ? ` ${styles.phoneInvalid}` : ''}`}
          onChange={(value) => updateField('phone', value)}
          onBlur={handleBlur}
        />
        {errors.phone && (
          <span className={styles.error} id="phone-error">
            {errors.phone}
          </span>
        )}
      </label>

      {submitError && (
        <p className={styles.formError} role="alert">
          {submitError}
        </p>
      )}

      <p className={styles.consent}>
        By signing in, you agree to be contacted about this event and similar homes. You can
        unsubscribe at any time.
      </p>

      <button className={styles.btnPrimary} type="submit" disabled={submitting}>
        {submitting ? 'Signing in...' : 'Sign in'}
      </button>
    </form>
  )
}

SignInForm.propTypes = {
  page: pagePropType.isRequired,
  onSuccess: PropTypes.func.isRequired,
}

function ThankYou({ page }) {
  const titleRef = useRef(null)

  useEffect(() => {
    window.scrollTo(0, 0)
    titleRef.current?.focus()
  }, [])

  return (
    <main className={`${styles.main} ${styles.mainThanks}`}>
      <section className={styles.thanksHero} aria-labelledby="thanks-title">
        <div className={styles.check}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0A0A47" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12l5 5 9-10" />
          </svg>
        </div>
        <h1 id="thanks-title" className={styles.thanksTitle} ref={titleRef} tabIndex={-1}>
          Thanks for joining us!
        </h1>
      </section>

      <AgentCard agent={page.agent} />
      {page.eventAddress && (
        <a className={styles.mapCard} href={mapUrl(page.eventAddress)} target="_blank" rel="noopener noreferrer">
          <PinIcon color="#2A3FBF" size={22} />
          <span>
            {page.eventAddress}
            <br />
            <span className={styles.mapCardAction}>Open in Maps</span>
          </span>
        </a>
      )}
    </main>
  )
}

ThankYou.propTypes = {
  page: pagePropType.isRequired,
}

function EventPage() {
  const { status, page, error } = usePublicPage()
  const [signedIn, setSignedIn] = useState(false)

  if (status !== 'success') return <PageStatus status={status} error={error} />

  return (
    <div className={styles.page}>
      {signedIn ? (
        <ThankYou page={page} />
      ) : (
        <main className={styles.main}>
          <div className={styles.column}>
            <section className={styles.welcome} aria-labelledby="welcome-title">
              <div className={styles.eyebrow}>{page.pageTypeName || 'Events'}</div>
              <h1 id="welcome-title" className={styles.title}>
              {page.eventName || page.pageTitle}
              </h1>
              
              <EventDetails page={page} />
              <AgentCard agent={page.agent} />
            </section>
          </div>
          <div className={styles.column}>
            <SignInForm page={page} onSuccess={() => setSignedIn(true)} />
          </div>
        </main>
      )}
      <div className={styles.footerNote}>Powered by Realty Space</div>
    </div>
  )
}

export default EventPage
